import React, { useState } from "react";
import {
  Search,
  Plus,
  Edit,
  Trash2,
  FileText,
  ShieldCheck,
  User,
  Key,
  Activity,
} from "lucide-react";
import MaritimeLayout from "../common/Applayout";

export default function NMCAdminRBACDashboard() {
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("users");

  const users = [
    {
      id: 1,
      name: "Ahmed Khan",
      email: "ahmed.khan@nmc.ae",
      role: "Port Admin",
      status: "Active",
      lastLogin: "2025-11-10 09:30",
    },
    {
      id: 2,
      name: "Fatima Al Mansoori",
      email: "fatima.moei@gov.ae",
      role: "MOEI Supervisor",
      status: "Active",
      lastLogin: "2025-11-10 08:15",
    },
    {
      id: 3,
      name: "John Doe",
      email: "john.doe@cebsworldwide.com",
      role: "System Operator",
      status: "Suspended",
      lastLogin: "2025-11-09 14:20",
    },
    {
      id: 4,
      name: "Maria Rodriguez",
      email: "maria.rodriguez@nmc.ae",
      role: "Port Admin",
      status: "Active",
      lastLogin: "2025-11-10 10:05",
    },
  ];

  const roles = [
    {
      name: "MOEI Supervisor",
      permissions: [
        "View All Data",
        "Modify Roles",
        "View Logs",
        "Approve Declarations",
      ],
      users: 12,
    },
    {
      name: "Port Admin",
      permissions: [
        "Create Declarations",
        "Approve Forms",
        "View Logs",
        "Manage Users",
      ],
      users: 8,
    },
    {
      name: "System Operator",
      permissions: [
        "Access Dashboard",
        "Manage Integrations",
        "System Monitoring",
      ],
      users: 5,
    },
    {
      name: "Auditor",
      permissions: ["View All Data", "Export Reports", "View Audit Logs"],
      users: 3,
    },
  ];

  const logs = [
    {
      id: 101,
      user: "Ahmed Khan",
      action: "Modified user role for John Doe",
      time: "2025-11-10 09:45",
      type: "System Log",
      severity: "medium",
    },
    {
      id: 102,
      user: "John Doe",
      action: "Accessed audit logs without authorization",
      time: "2025-11-10 09:50",
      type: "Security Log",
      severity: "high",
    },
    {
      id: 103,
      user: "Fatima Al Mansoori",
      action: "Deleted inactive account #452",
      time: "2025-11-10 09:55",
      type: "User Log",
      severity: "low",
    },
    {
      id: 104,
      user: "Maria Rodriguez",
      action: "Created new vessel declaration",
      time: "2025-11-10 10:15",
      type: "System Log",
      severity: "low",
    },
  ];

  const stats = {
    totalUsers: 28,
    activeUsers: 25,
    suspendedUsers: 3,
    totalRoles: 4,
    todayLogs: 47,
  };

  const styles = {
    container: {
      minHeight: "100vh",
      background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
      padding: "24px",
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    },
    header: {
      marginBottom: "32px",
    },
    headerContent: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      marginBottom: "12px",
    },
    headerIcon: {
      padding: "8px",
      background: "#2563eb",
      borderRadius: "8px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    title: {
      fontSize: "28px",
      fontWeight: "700",
      color: "#1e293b",
      margin: "0",
    },
    subtitle: {
      color: "#64748b",
      fontSize: "16px",
      lineHeight: "1.5",
      maxWidth: "600px",
      margin: "0",
    },
    statsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
      gap: "16px",
      marginBottom: "32px",
    },
    statCard: {
      background: "#ffffff",
      borderRadius: "12px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      border: "1px solid #e2e8f0",
      padding: "16px",
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    statIcon: {
      padding: "8px",
      borderRadius: "8px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    statText: {
      display: "flex",
      flexDirection: "column",
    },
    statLabel: {
      fontSize: "14px",
      fontWeight: "500",
      color: "#64748b",
      margin: "0",
    },
    statValue: {
      fontSize: "24px",
      fontWeight: "700",
      color: "#1e293b",
      margin: "0",
    },
    mainCard: {
      background: "#ffffff",
      borderRadius: "12px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      border: "1px solid #e2e8f0",
      overflow: "hidden",
    },
    tabs: {
      display: "flex",
      borderBottom: "1px solid #e2e8f0",
    },
    tab: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "16px 24px",
      fontWeight: "500",
      borderBottom: "2px solid transparent",
      background: "none",
      border: "none",
      cursor: "pointer",
      transition: "all 0.2s ease",
      fontSize: "14px",
    },
    tabActive: {
      borderBottomColor: "#2563eb",
      color: "#2563eb",
      background: "#dbeafe",
    },
    tabInactive: {
      color: "#64748b",
      background: "transparent",
    },
    tabContent: {
      padding: "24px",
    },
    searchSection: {
      display: "flex",
      flexDirection: "column",
      gap: "16px",
      marginBottom: "24px",
    },
    searchRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: "16px",
      flexWrap: "wrap",
    },
    searchContainer: {
      position: "relative",
      flex: "1",
      minWidth: "300px",
    },
    searchInput: {
      width: "80%",
      padding: "8px 8px 8px 36px",
      border: "1px solid #d1d5db",
      borderRadius: "8px",
      fontSize: "14px",
      outline: "none",
      transition: "border-color 0.2s ease",
    },
    searchInputFocus: {
      borderColor: "#2563eb",
      boxShadow: "0 0 0 2px rgba(37, 99, 235, 0.1)",
    },
    searchIcon: {
      position: "absolute",
      left: "12px",
      top: "50%",
      transform: "translateY(-50%)",
      color: "#64748b",
    },
    button: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "8px 16px",
      borderRadius: "8px",
      border: "none",
      fontSize: "14px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.2s ease",
    },
    buttonPrimary: {
      background: "#2563eb",
      color: "white",
    },
    buttonSecondary: {
      background: "transparent",
      border: "1px solid #d1d5db",
      color: "#374151",
    },
    buttonSuccess: {
      background: "#059669",
      color: "white",
    },
    buttonHover: {
      transform: "translateY(-1px)",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      background: "#ffffff",
      borderRadius: "8px",
      overflow: "hidden",
    },
    tableHeader: {
      background: "#f8fafc",
      borderBottom: "1px solid #e2e8f0",
    },
    tableHeaderCell: {
      padding: "12px 16px",
      textAlign: "left",
      fontSize: "12px",
      fontWeight: "600",
      color: "#64748b",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
    },
    tableRow: {
      borderBottom: "1px solid #f1f5f9",
      transition: "background-color 0.2s ease",
    },
    tableRowHover: {
      background: "#f8fafc",
    },
    tableCell: {
      padding: "16px",
      fontSize: "14px",
      color: "#374151",
    },
    badge: {
      display: "inline-flex",
      alignItems: "center",
      padding: "4px 12px",
      borderRadius: "9999px",
      fontSize: "12px",
      fontWeight: "500",
    },
    badgeActive: {
      background: "#dcfce7",
      color: "#166534",
    },
    badgeSuspended: {
      background: "#fee2e2",
      color: "#dc2626",
    },
    badgeRole: {
      background: "#dbeafe",
      color: "#1e40af",
    },
    badgeLow: {
      background: "#dcfce7",
      color: "#166534",
    },
    badgeMedium: {
      background: "#fef3c7",
      color: "#92400e",
    },
    badgeHigh: {
      background: "#fee2e2",
      color: "#dc2626",
    },
    badgeType: {
      background: "#f1f5f9",
      color: "#475569",
    },
    rolesGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
      gap: "20px",
    },
    roleCard: {
      background: "#ffffff",
      border: "1px solid #e2e8f0",
      borderRadius: "12px",
      padding: "20px",
      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
      transition: "box-shadow 0.2s ease",
    },
    roleCardHover: {
      boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
    },
    roleHeader: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      marginBottom: "16px",
    },
    roleColor: {
      width: "12px",
      height: "12px",
      borderRadius: "50%",
      flexShrink: "0",
    },
    roleName: {
      fontSize: "16px",
      fontWeight: "600",
      color: "#1e293b",
      margin: "0",
    },
    roleUsers: {
      marginLeft: "auto",
      fontSize: "12px",
      color: "#64748b",
      background: "#f1f5f9",
      padding: "4px 8px",
      borderRadius: "6px",
    },
    permissionsList: {
      listStyle: "none",
      padding: "0",
      margin: "0 0 16px 0",
    },
    permissionItem: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontSize: "14px",
      color: "#475569",
      marginBottom: "8px",
    },
    permissionDot: {
      width: "6px",
      height: "6px",
      background: "#10b981",
      borderRadius: "50%",
      flexShrink: "0",
    },
    actionButtons: {
      display: "flex",
      gap: "8px",
      paddingTop: "16px",
      borderTop: "1px solid #f1f5f9",
    },
    actionButton: {
      flex: "1",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "6px",
      padding: "8px 12px",
      fontSize: "12px",
      background: "transparent",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      transition: "all 0.2s ease",
    },
    actionButtonEdit: {
      color: "#374151",
    },
    actionButtonDelete: {
      color: "#374151",
    },
    actionButtonHover: {
      background: "#f8fafc",
    },
  };

  const getRoleColor = (index) => {
    const colors = ["#3b82f6", "#10b981", "#8b5cf6", "#f59e0b"];
    return colors[index % colors.length];
  };

  return (
    <MaritimeLayout>
      {" "}
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <div style={styles.headerIcon}>
              <ShieldCheck size={28} color="white" />
            </div>
            <h1 style={styles.title}>
              NMC Admin Console – Role-Based Access Control
            </h1>
          </div>
          <p style={styles.subtitle}>
            Manage users, roles, and permissions for the National Maritime
            Center. Track all user, system, and access logs for full audit
            compliance with UAE government security standards.
          </p>
        </div>

        {/* Stats Overview */}
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={{ ...styles.statIcon, background: "#dbeafe" }}>
              <User size={20} color="#2563eb" />
            </div>
            <div style={styles.statText}>
              <p style={styles.statLabel}>Total Users</p>
              <p style={styles.statValue}>{stats.totalUsers}</p>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={{ ...styles.statIcon, background: "#dcfce7" }}>
              <Activity size={20} color="#059669" />
            </div>
            <div style={styles.statText}>
              <p style={styles.statLabel}>Active Users</p>
              <p style={styles.statValue}>{stats.activeUsers}</p>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={{ ...styles.statIcon, background: "#fee2e2" }}>
              <User size={20} color="#dc2626" />
            </div>
            <div style={styles.statText}>
              <p style={styles.statLabel}>Suspended</p>
              <p style={styles.statValue}>{stats.suspendedUsers}</p>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={{ ...styles.statIcon, background: "#f3e8ff" }}>
              <Key size={20} color="#7c3aed" />
            </div>
            <div style={styles.statText}>
              <p style={styles.statLabel}>Roles</p>
              <p style={styles.statValue}>{stats.totalRoles}</p>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={{ ...styles.statIcon, background: "#ffedd5" }}>
              <FileText size={20} color="#ea580c" />
            </div>
            <div style={styles.statText}>
              <p style={styles.statLabel}>Today's Logs</p>
              <p style={styles.statValue}>{stats.todayLogs}</p>
            </div>
          </div>
        </div>

        {/* Main Content Card */}
        <div style={styles.mainCard}>
          {/* Tabs Navigation */}
          <div style={styles.tabs}>
            <button
              style={{
                ...styles.tab,
                ...(activeTab === "users"
                  ? styles.tabActive
                  : styles.tabInactive),
              }}
              onClick={() => setActiveTab("users")}
            >
              <User size={18} />
              Users Management
            </button>
            <button
              style={{
                ...styles.tab,
                ...(activeTab === "roles"
                  ? styles.tabActive
                  : styles.tabInactive),
              }}
              onClick={() => setActiveTab("roles")}
            >
              <Key size={18} />
              Roles & Permissions
            </button>
            <button
              style={{
                ...styles.tab,
                ...(activeTab === "logs"
                  ? styles.tabActive
                  : styles.tabInactive),
              }}
              onClick={() => setActiveTab("logs")}
            >
              <Activity size={18} />
              Audit & Access Logs
            </button>
          </div>

          <div style={styles.tabContent}>
            {/* Users Tab */}
            {activeTab === "users" && (
              <div>
                <div style={styles.searchSection}>
                  <div style={styles.searchRow}>
                    <div style={styles.searchContainer}>
                      <Search size={18} style={styles.searchIcon} />
                      <input
                        type="text"
                        placeholder="Search users by name, email, or role..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={styles.searchInput}
                        onFocus={(e) =>
                          (e.target.style.borderColor = "#2563eb")
                        }
                        onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
                      />
                    </div>
                    <button
                      style={{ ...styles.button, ...styles.buttonPrimary }}
                    >
                      <Plus size={18} />
                      Add New User
                    </button>
                  </div>
                </div>

                <table style={styles.table}>
                  <thead style={styles.tableHeader}>
                    <tr>
                      <th style={styles.tableHeaderCell}>User</th>
                      <th style={styles.tableHeaderCell}>Role</th>
                      <th style={styles.tableHeaderCell}>Status</th>
                      <th style={styles.tableHeaderCell}>Last Login</th>
                      <th style={styles.tableHeaderCell}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id} style={styles.tableRow}>
                        <td style={styles.tableCell}>
                          <div>
                            <div
                              style={{ fontWeight: "500", color: "#1e293b" }}
                            >
                              {user.name}
                            </div>
                            <div style={{ fontSize: "12px", color: "#64748b" }}>
                              {user.email}
                            </div>
                          </div>
                        </td>
                        <td style={styles.tableCell}>
                          <span
                            style={{ ...styles.badge, ...styles.badgeRole }}
                          >
                            {user.role}
                          </span>
                        </td>
                        <td style={styles.tableCell}>
                          <span
                            style={{
                              ...styles.badge,
                              ...(user.status === "Active"
                                ? styles.badgeActive
                                : styles.badgeSuspended),
                            }}
                          >
                            {user.status}
                          </span>
                        </td>
                        <td style={styles.tableCell}>
                          <span style={{ fontSize: "12px", color: "#64748b" }}>
                            {user.lastLogin}
                          </span>
                        </td>
                        <td style={styles.tableCell}>
                          <div style={{ display: "flex", gap: "8px" }}>
                            <button
                              style={{
                                ...styles.actionButton,
                                ...styles.actionButtonEdit,
                              }}
                            >
                              <Edit size={14} />
                            </button>
                            <button
                              style={{
                                ...styles.actionButton,
                                ...styles.actionButtonDelete,
                              }}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Roles Tab */}
            {activeTab === "roles" && (
              <div>
                <div style={styles.searchRow}>
                  <h2
                    style={{
                      fontSize: "24px",
                      fontWeight: "700",
                      color: "#1e293b",
                      margin: "0",
                    }}
                  >
                    Defined Roles & Permissions
                  </h2>
                  <button style={{ ...styles.button, ...styles.buttonPrimary }}>
                    <Plus size={18} />
                    Add New Role
                  </button>
                </div>

                <div style={styles.rolesGrid}>
                  {roles.map((role, index) => (
                    <div key={index} style={styles.roleCard}>
                      <div style={styles.roleHeader}>
                        <div
                          style={{
                            ...styles.roleColor,
                            background: getRoleColor(index),
                          }}
                        />
                        <h3 style={styles.roleName}>{role.name}</h3>
                        <span style={styles.roleUsers}>{role.users} users</span>
                      </div>
                      <ul style={styles.permissionsList}>
                        {role.permissions.map((permission, idx) => (
                          <li key={idx} style={styles.permissionItem}>
                            <div style={styles.permissionDot} />
                            {permission}
                          </li>
                        ))}
                      </ul>
                      <div style={styles.actionButtons}>
                        <button
                          style={{
                            ...styles.actionButton,
                            ...styles.actionButtonEdit,
                          }}
                        >
                          <Edit size={14} />
                          Edit
                        </button>
                        <button
                          style={{
                            ...styles.actionButton,
                            ...styles.actionButtonDelete,
                          }}
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Logs Tab */}
            {activeTab === "logs" && (
              <div>
                <div style={styles.searchRow}>
                  <h2
                    style={{
                      fontSize: "24px",
                      fontWeight: "700",
                      color: "#1e293b",
                      margin: "0",
                    }}
                  >
                    System, User & Access Logs
                  </h2>
                  <button
                    style={{ ...styles.button, ...styles.buttonSecondary }}
                  >
                    <FileText size={18} />
                    Export Logs (CSV/PDF)
                  </button>
                </div>

                <table style={{ ...styles.table, marginTop: "20px" }}>
                  <thead style={styles.tableHeader}>
                    <tr>
                      <th style={styles.tableHeaderCell}>Time</th>
                      <th style={styles.tableHeaderCell}>User</th>
                      <th style={styles.tableHeaderCell}>Action</th>
                      <th style={styles.tableHeaderCell}>Type</th>
                      <th style={styles.tableHeaderCell}>Severity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr key={log.id} style={styles.tableRow}>
                        <td style={styles.tableCell}>
                          <span style={{ fontWeight: "500", color: "#1e293b" }}>
                            {log.time}
                          </span>
                        </td>
                        <td style={styles.tableCell}>{log.user}</td>
                        <td style={styles.tableCell}>{log.action}</td>
                        <td style={styles.tableCell}>
                          <span
                            style={{ ...styles.badge, ...styles.badgeType }}
                          >
                            {log.type}
                          </span>
                        </td>
                        <td style={styles.tableCell}>
                          <span
                            style={{
                              ...styles.badge,
                              ...(log.severity === "low"
                                ? styles.badgeLow
                                : log.severity === "medium"
                                ? styles.badgeMedium
                                : styles.badgeHigh),
                            }}
                          >
                            {log.severity.charAt(0).toUpperCase() +
                              log.severity.slice(1)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginTop: "24px",
                  }}
                >
                  <button style={{ ...styles.button, ...styles.buttonSuccess }}>
                    <ShieldCheck size={18} />
                    View Security & Access Policy
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </MaritimeLayout>
  );
}
